import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Loader2, Save, Film, PlayCircle, ExternalLink, UploadCloud, FileVideo, Link as LinkIcon
} from 'lucide-react';
import { API } from '../App';
import { useTranslation } from '../i18n';

const adminAxios = axios.create();
adminAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

/** Resolve a URL stored in settings — relative /api/* gets prefixed with backend. */
const resolveUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('/api/')) return `${BACKEND}${u}`;
  return u;
};

const MAX_BYTES = 100 * 1024 * 1024; // 100 MB

const AdminContent = () => {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewKey, setPreviewKey] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [mode, setMode] = useState('upload'); // 'upload' | 'url'
  const fileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/settings/intro-video`)
      .then(res => {
        if (cancelled) return;
        setUrl(res.data.url || '');
        setEnabled(!!res.data.enabled);
      })
      .catch(() => {
        if (!cancelled) toast.error('Erro ao carregar configurações');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    const trimmed = url.trim();
    if (trimmed && !/^https?:\/\//i.test(trimmed) && !trimmed.startsWith('/api/')) {
      toast.error('URL inválida — deve começar com http:// ou https://');
      return;
    }
    setSaving(true);
    try {
      const res = await adminAxios.put(`${API}/admin/settings/intro-video`, { url: trimmed, enabled });
      setUrl(res.data.url || '');
      setEnabled(!!res.data.enabled);
      setPreviewKey(k => k + 1);
      toast.success('Configurações salvas!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    if (!/^video\/(mp4|quicktime|webm|x-m4v)$/.test(file.type) && !/\.(mp4|mov|webm|m4v)$/i.test(file.name)) {
      toast.error('Formato inválido. Use .mp4, .mov, .webm ou .m4v');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`Arquivo muito grande (${(file.size / 1024 / 1024).toFixed(1)}MB). Máximo: 100MB`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await adminAxios.post(
        `${API}/admin/settings/intro-video/upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
          },
        }
      );
      setUrl(res.data.url || '');
      setEnabled(!!res.data.enabled);
      setPreviewKey(k => k + 1);
      toast.success(`Vídeo enviado! (${(file.size / 1024 / 1024).toFixed(1)}MB)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha no upload');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (f) handleUpload(f);
    e.target.value = ''; // reset for re-selecting same file
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleUpload(f);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const resolvedUrl = resolveUrl(url);

  return (
    <div className="space-y-6" data-testid="admin-content-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {t('admin.content.title')}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {t('admin.content.subtitle')}
        </p>
      </div>

      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <Film className="w-5 h-5 text-blue-600" />
            {t('admin.content.videoCard')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Enabled toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
            <div>
              <p className="text-sm font-semibold text-slate-900">{t('admin.content.showOnLanding')}</p>
              <p className="text-xs text-slate-500 mt-0.5">{t('admin.content.showOnLandingHint')}</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              onClick={() => setEnabled(v => !v)}
              data-testid="content-video-toggle"
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${enabled ? 'bg-blue-600' : 'bg-slate-300'}`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition ${enabled ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>

          {/* Mode tabs */}
          <div className="flex gap-2 border-b border-slate-200">
            <button
              onClick={() => setMode('upload')}
              data-testid="content-tab-upload"
              className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                mode === 'upload' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <UploadCloud className="w-4 h-4" /> {t('admin.content.tabUpload')}
            </button>
            <button
              onClick={() => setMode('url')}
              data-testid="content-tab-url"
              className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                mode === 'url' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <LinkIcon className="w-4 h-4" /> {t('admin.content.tabUrl')}
            </button>
          </div>

          {/* Upload zone */}
          {mode === 'upload' && (
            <div className="space-y-3">
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                data-testid="content-video-dropzone"
                className={`relative border-2 border-dashed rounded-xl p-8 sm:p-10 text-center cursor-pointer transition-colors ${
                  dragOver
                    ? 'border-blue-500 bg-blue-50/50'
                    : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm,video/x-m4v,.mp4,.mov,.webm,.m4v"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="content-video-file-input"
                />
                {uploading ? (
                  <div className="space-y-3">
                    <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto" />
                    <p className="text-sm font-medium text-slate-700">{t('admin.content.uploading')}</p>
                    <div className="max-w-xs mx-auto">
                      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-600 transition-all duration-200"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-1.5">{uploadProgress}%</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-50 rounded-full">
                      <FileVideo className="w-7 h-7 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">
                        {t('admin.content.dropzone')}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {t('admin.content.dropzoneHint')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-500 text-center">
                {t('admin.content.storageInfo')}
              </p>
            </div>
          )}

          {/* URL paste */}
          {mode === 'url' && (
            <div className="space-y-2">
              <Label htmlFor="video-url" className="text-sm font-semibold text-slate-700">
                {t('admin.content.urlLabel')}
              </Label>
              <Input
                id="video-url"
                data-testid="content-video-url-input"
                type="url"
                placeholder="https://..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="font-mono text-sm"
              />
              <p className="text-xs text-slate-500">
                {t('admin.content.urlHint')}
              </p>
            </div>
          )}

          {/* Save button */}
          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Button
              onClick={handleSave}
              disabled={saving || uploading}
              data-testid="content-video-save-btn"
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('common.saving')}</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> {t('admin.content.saveChanges')}</>
              )}
            </Button>
            {resolvedUrl && (
              <a
                href={resolvedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-100 border border-slate-200"
                data-testid="content-video-open-link"
              >
                <ExternalLink className="w-4 h-4" />
                {t('admin.content.openCurrent')}
              </a>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      {resolvedUrl && (
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <PlayCircle className="w-5 h-5 text-emerald-600" />
              {t('admin.content.preview')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-black ring-1 ring-slate-200">
              <video
                key={previewKey}
                data-testid="content-video-preview"
                src={resolvedUrl}
                controls
                preload="metadata"
                playsInline
                className="absolute inset-0 w-full h-full object-contain"
              />
            </div>
            <p className="text-xs text-slate-500 mt-3 break-all">
              {enabled ? t('admin.content.activeMsg') : t('admin.content.inactiveMsg')}
              {' · '}
              <span className="font-mono text-slate-400">{url}</span>
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminContent;
